import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AlertOctagon, CheckCircle2, GitCompare, Lock, RefreshCw, ShieldCheck } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString('es-ES', { dateStyle: 'short', timeStyle: 'short' }) : '—');

const statusStyle = {
  open:   'bg-amber-50 text-amber-700 border-amber-200',
  closed: 'bg-slate-100 text-slate-700 border-slate-300',
};

const kindStyle = {
  provider:    'bg-violet-50 text-violet-700 border-violet-200',
  payment:     'bg-blue-50 text-blue-700 border-blue-200',
  operational: 'bg-emerald-50 text-emerald-700 border-emerald-200',
};

const FinanceReconciliationsPage = () => {
  const { getAuthHeaders } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [kindFilter, setKindFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [detail, setDetail] = useState(null);

  const fetchSessions = async () => {
    setLoading(true);
    try {
      const params = { limit: 200 };
      if (kindFilter !== 'all') params.kind = kindFilter;
      if (statusFilter !== 'all') params.status = statusFilter;
      const r = await axios.get(`${API}/finance/reconciliations`, { headers: getAuthHeaders(), params });
      setSessions(r.data.sessions || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchSessions(); /* eslint-disable-next-line */ }, [kindFilter, statusFilter]);

  const stats = useMemo(() => {
    const open = sessions.filter((s) => s.status === 'open').length;
    const closed = sessions.filter((s) => s.status === 'closed').length;
    const totalMismatches = sessions.reduce((a, s) => a + (s.mismatches?.length || 0), 0);
    const unresolved = sessions.reduce((a, s) => a + (s.mismatches?.filter((m) => !m.resolved).length || 0), 0);
    return { open, closed, totalMismatches, unresolved };
  }, [sessions]);

  return (
    <div className="space-y-6" data-testid="finance-reconciliations-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <GitCompare className="w-6 h-6 text-indigo-600" />
            Reconciliaciones
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            Sesiones manuales de reconciliación con detección de mismatches. Sin auto-fix.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="bg-slate-50 border-slate-200 text-slate-700 gap-1">
            <Lock className="w-3 h-3" /> Read-only
          </Badge>
          <Badge variant="outline" className="bg-emerald-50 border-emerald-200 text-emerald-700 gap-1">
            <ShieldCheck className="w-3 h-3" /> Solo observación
          </Badge>
          <Button size="sm" variant="outline" onClick={fetchSessions} data-testid="recon-refresh-btn">
            <RefreshCw className="w-4 h-4 mr-1" /> Refrescar
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="border-0 shadow-sm p-3" data-testid="recon-stat-open">
          <p className="text-xs text-slate-500 uppercase tracking-wider">Abiertas</p>
          <p className="text-2xl font-bold text-amber-700">{stats.open}</p>
        </Card>
        <Card className="border-0 shadow-sm p-3" data-testid="recon-stat-closed">
          <p className="text-xs text-slate-500 uppercase tracking-wider">Cerradas</p>
          <p className="text-2xl font-bold text-slate-700">{stats.closed}</p>
        </Card>
        <Card className="border-0 shadow-sm p-3" data-testid="recon-stat-mismatches">
          <p className="text-xs text-slate-500 uppercase tracking-wider">Mismatches totales</p>
          <p className="text-2xl font-bold text-rose-700">{stats.totalMismatches}</p>
        </Card>
        <Card className="border-0 shadow-sm p-3" data-testid="recon-stat-unresolved">
          <p className="text-xs text-slate-500 uppercase tracking-wider">Sin resolver</p>
          <p className="text-2xl font-bold text-rose-800">{stats.unresolved}</p>
        </Card>
      </div>

      {/* Filters */}
      <Card className="border-0 shadow-sm p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-slate-500 font-medium">Tipo</label>
            <Select value={kindFilter} onValueChange={setKindFilter}>
              <SelectTrigger data-testid="recon-kind-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="provider">provider</SelectItem>
                <SelectItem value="payment">payment</SelectItem>
                <SelectItem value="operational">operational</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs text-slate-500 font-medium">Estado</label>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger data-testid="recon-status-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="open">open</SelectItem>
                <SelectItem value="closed">closed</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </Card>

      {/* Sessions */}
      <Card className="border-0 shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center p-12">
            <div className="w-8 h-8 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : sessions.length === 0 ? (
          <div className="p-12 text-center text-slate-500">
            <GitCompare className="w-12 h-12 mx-auto text-slate-300 mb-3" />
            <p>Sin sesiones de reconciliación</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 border-b text-left">
                  <th className="p-3 font-medium text-slate-600">Estado</th>
                  <th className="p-3 font-medium text-slate-600">Tipo</th>
                  <th className="p-3 font-medium text-slate-600">Scope</th>
                  <th className="p-3 font-medium text-slate-600">Actor</th>
                  <th className="p-3 font-medium text-slate-600">Mismatches</th>
                  <th className="p-3 font-medium text-slate-600">Abierta</th>
                  <th className="p-3 font-medium text-slate-600">Cerrada</th>
                </tr>
              </thead>
              <tbody>
                {sessions.map((s) => {
                  const unresolved = (s.mismatches || []).filter((m) => !m.resolved).length;
                  return (
                    <tr
                      key={s.id}
                      className="border-b hover:bg-slate-50 cursor-pointer"
                      onClick={() => setDetail(s)}
                      data-testid={`recon-row-${s.id}`}
                    >
                      <td className="p-3">
                        <span className={`px-2 py-0.5 text-[10px] font-semibold rounded border ${statusStyle[s.status]}`}>{s.status}</span>
                      </td>
                      <td className="p-3">
                        <span className={`px-2 py-0.5 text-[10px] font-semibold rounded border ${kindStyle[s.kind] || 'bg-slate-50 border-slate-200 text-slate-600'}`}>{s.kind}</span>
                      </td>
                      <td className="p-3 text-xs text-slate-600 font-mono max-w-xs truncate">{JSON.stringify(s.scope || {})}</td>
                      <td className="p-3 font-mono text-xs text-slate-500">{s.actor_id?.slice(0, 8)}…</td>
                      <td className="p-3 text-sm">
                        <span className="text-slate-700">{(s.mismatches || []).length}</span>
                        {unresolved > 0 && (
                          <span className="ml-2 inline-flex items-center gap-1 text-rose-600 text-xs font-semibold">
                            <AlertOctagon className="w-3 h-3" /> {unresolved} sin resolver
                          </span>
                        )}
                      </td>
                      <td className="p-3 text-slate-400 whitespace-nowrap text-xs">{fmtDate(s.created_at)}</td>
                      <td className="p-3 text-slate-400 whitespace-nowrap text-xs">{fmtDate(s.closed_at)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Detail dialog */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto" data-testid="recon-detail-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <GitCompare className="w-5 h-5 text-indigo-600" />
              Sesión de reconciliación
              {detail && <span className={`px-2 py-0.5 text-[10px] font-semibold rounded border ${statusStyle[detail.status]}`}>{detail.status}</span>}
              {detail && <span className={`px-2 py-0.5 text-[10px] font-semibold rounded border ${kindStyle[detail.kind] || 'bg-slate-50 border-slate-200 text-slate-600'}`}>{detail.kind}</span>}
            </DialogTitle>
          </DialogHeader>
          {detail && (
            <div className="space-y-4 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <div><p className="text-xs text-slate-500">ID</p><p className="font-mono text-xs break-all">{detail.id}</p></div>
                <div><p className="text-xs text-slate-500">Actor</p><p className="font-mono text-xs">{detail.actor_id}</p></div>
                <div className="col-span-2">
                  <p className="text-xs text-slate-500">Scope</p>
                  <pre className="font-mono text-[11px] bg-slate-50 p-2 rounded border max-h-32 overflow-y-auto">{JSON.stringify(detail.scope, null, 2)}</pre>
                </div>
                {detail.notes && <div className="col-span-2"><p className="text-xs text-slate-500">Notas</p><p className="text-slate-700">{detail.notes}</p></div>}
              </div>

              <div>
                <p className="text-xs font-semibold text-slate-600 mb-2">Mismatches ({(detail.mismatches || []).length})</p>
                {(detail.mismatches || []).length === 0 ? (
                  <div className="p-4 text-center text-slate-400 border rounded-lg bg-slate-50 text-xs">
                    <CheckCircle2 className="w-5 h-5 mx-auto text-emerald-500 mb-1" /> Sin mismatches registrados
                  </div>
                ) : (
                  <div className="space-y-2">
                    {(detail.mismatches || []).map((m) => (
                      <div key={m.id} className="border rounded-lg p-3 bg-rose-50/50">
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <p className="font-semibold text-rose-800 text-xs flex items-center gap-1">
                              <AlertOctagon className="w-3 h-3" />
                              {m.kind} · {m.reference_id}
                            </p>
                            <p className="text-[10px] text-slate-400 mt-0.5">{fmtDate(m.added_at)}</p>
                          </div>
                          {m.resolved ? (
                            <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px]">Resuelto</Badge>
                          ) : (
                            <Badge variant="outline" className="bg-rose-50 text-rose-700 border-rose-200 text-[10px]">Pendiente</Badge>
                          )}
                        </div>
                        <div className="grid grid-cols-2 gap-2 text-xs">
                          <div>
                            <p className="text-slate-500">Esperado</p>
                            <pre className="font-mono text-[10px] bg-white p-2 rounded border">{JSON.stringify(m.expected, null, 2)}</pre>
                          </div>
                          <div>
                            <p className="text-slate-500">Actual</p>
                            <pre className="font-mono text-[10px] bg-white p-2 rounded border">{JSON.stringify(m.actual, null, 2)}</pre>
                          </div>
                        </div>
                        {m.note && <p className="text-xs text-slate-600 mt-2 italic">"{m.note}"</p>}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {detail.summary && Object.keys(detail.summary || {}).length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-slate-600 mb-1">Resumen al cierre</p>
                  <pre className="font-mono text-[11px] bg-slate-50 p-2 rounded border">{JSON.stringify(detail.summary, null, 2)}</pre>
                </div>
              )}

              <p className="text-[11px] text-slate-400 italic flex items-center gap-1">
                <Lock className="w-3 h-3" /> Sesión inmutable. Las correcciones se aplican siempre mediante nuevas transacciones en el libro mayor.
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FinanceReconciliationsPage;
